const getDefaultWindow = () => (typeof window !== 'undefined' ? window : null);

const getAnalyticsConfig = () => {
  try {
    return getDefaultWindow()?.analyticsConfig;
  } catch (_) {
    return undefined;
  }
};

/**
 * AnalyticsHelper class to initialize and track user analytics
 * @class AnalyticsHelper
 */
const MAX_PENDING_ACTIONS = 50;

export class AnalyticsHelper {
  /**
   * @constructor
   * @param {Object} [options={}] - options for analytics
   * @param {string} [options.token] - analytics token
   */
  constructor({ token: analyticsToken } = {}) {
    this.analyticsToken = analyticsToken;
    this.analytics = null;
    this.loadingPromise = null;
    this.pendingActions = [];
    this.user = {};
  }

  /**
   * Initialize analytics
   * @function
   * @async
   */
  init() {
    if (!this.analyticsToken) {
      return Promise.resolve(null);
    }

    if (this.analytics) {
      return Promise.resolve(this.analytics);
    }

    if (this.loadingPromise) {
      return this.loadingPromise;
    }

    this.loadingPromise = import(
      /* webpackChunkName: "analytics" */ '@june-so/analytics-next'
    )
      .then(({ AnalyticsBrowser }) =>
        AnalyticsBrowser.load({
          writeKey: this.analyticsToken,
        })
      )
      .then(([analytics]) => {
        this.analytics = analytics;
        this.flushPendingActions();
        return analytics;
      })
      .catch(() => {
        this.pendingActions = [];
      })
      .finally(() => {
        this.loadingPromise = null;
      });

    return this.loadingPromise;
  }

  runWhenReady(action) {
    if (this.analytics) {
      action();
      return;
    }

    if (!this.analyticsToken) {
      return;
    }

    if (!this.loadingPromise) {
      this.init();
    }

    if (this.pendingActions.length < MAX_PENDING_ACTIONS) {
      this.pendingActions.push(action);
    }
  }

  flushPendingActions() {
    const pendingActions = [...this.pendingActions];
    this.pendingActions = [];
    pendingActions.forEach((action) => action());
  }

  /**
   * Identify the user
   * @function
   * @param {Object} user - User object
   */
  identify(user) {
    this.runWhenReady(() => {
      this.user = user;
      this.analytics.identify(this.user.email, {
        userId: this.user.id,
        email: this.user.email,
        name: this.user.name,
        avatar: this.user.avatar_url,
      });

      const { accounts, account_id: accountId } = this.user;
      const [currentAccount] = accounts.filter(
        (account) => account.id === accountId
      );
      if (currentAccount) {
        this.analytics.group(currentAccount.id, this.user.id, {
          name: currentAccount.name,
        });
      }
    });
  }

  /**
   * Track any event
   * @function
   * @param {string} eventName - event name
   * @param {Object} [properties={}] - event properties
   */
  track(eventName, properties = {}) {
    this.runWhenReady(() => {
      this.analytics.track({
        userId: this.user.id,
        event: eventName,
        properties,
      });
    });
  }

  /**
   * Track the page views
   * @function
   * @param {Object} params - Page view properties
   */
  page(params) {
    this.runWhenReady(() => {
      this.analytics.page(params);
    });
  }
}

// This object is shared across, the init is called in app/javascript/packs/application.js
export default new AnalyticsHelper(getAnalyticsConfig());
